import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["iconName", "label"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/*
 * © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import TextField from '@mui/material/TextField';
import MuiButton from '@mui/material/Button';
import { visuallyHidden } from '@mui/utils';
import { EmbersMenuItem } from '../menuItem/menuItem';
import { scrollMenuItemIntoView } from '../menuItem/scrollMenuItemIntoViewUtil';
import { StyledMenu, SearchContainer } from './searchStyles';
var ActionButton = function ActionButton(_ref) {
  var iconName = _ref.iconName,
    label = _ref.label,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(MuiButton, _extends({
    "aria-label": label,
    size: "small",
    variant: "text",
    disableRipple: true
  }, otherProps), /*#__PURE__*/React.createElement("i", {
    role: "none",
    className: iconName
  }));
};
var RECENT_SEARCH_LABEL = 'RECENT_SEARCH_LABEL';
export var EmbersSearch = function EmbersSearch(_ref2) {
  var storageKey = _ref2.storageKey,
    placeholder = _ref2.placeholder,
    searchTitle = _ref2.searchTitle,
    onSearch = _ref2.onSearch,
    className = _ref2.className,
    _ref2$options = _ref2.options,
    options = _ref2$options === void 0 ? [] : _ref2$options,
    _ref2$defaultSearchTe = _ref2.defaultSearchText,
    defaultSearchText = _ref2$defaultSearchTe === void 0 ? '' : _ref2$defaultSearchTe,
    listboxId = _ref2.listboxId;
  var intl = useIntl();
  var searchListboxId = listboxId || 'EmbersSearchOptionItemList';
  var getLocalRecentSearches = function getLocalRecentSearches() {
    var _ref3;
    return ((_ref3 = localStorage.getItem(storageKey) || undefined) === null || _ref3 === void 0 ? void 0 : _ref3.split(',')) || [];
  };
  var _useState = useState(defaultSearchText),
    _useState2 = _slicedToArray(_useState, 2),
    searchText = _useState2[0],
    setSearchText = _useState2[1];
  var _useState3 = useState(getLocalRecentSearches()),
    _useState4 = _slicedToArray(_useState3, 2),
    recentSearches = _useState4[0],
    setRecentSearches = _useState4[1];
  var _useState5 = useState(recentSearches.length > 0 && !searchText),
    _useState6 = _slicedToArray(_useState5, 2),
    showRecentSearchLabel = _useState6[0],
    setShowRecentSearchLabel = _useState6[1];
  var _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    filterOptions = _useState8[0],
    setFilterOptions = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    optionsMenuOpen = _useState10[0],
    setOptionsMenuOpen = _useState10[1];
  var optionsMenuRef = useRef(null);
  var inputRef = useRef(null);
  // save the behavior when trigger clear
  var clearBtnTriggerBehavior = useRef('');
  // save the target when hit 'Enter'
  var enterTarget = useRef('');
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    focus = _useState12[0],
    setFocus = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    clickFocus = _useState14[0],
    setClickFocus = _useState14[1];
  var searchTimeoutHandle = useRef(undefined);
  var _useState15 = useState({
      text: '',
      deleteFocus: false
    }),
    _useState16 = _slicedToArray(_useState15, 2),
    focusOptionInfo = _useState16[0],
    setFocusOptionInfo = _useState16[1];
  var isExpand = optionsMenuOpen && filterOptions.length > 0;
  var optionCount = filterOptions[0] === RECENT_SEARCH_LABEL ? filterOptions.length - 1 : filterOptions.length;
  useEffect(function () {
    setSearchText(defaultSearchText);
  }, [defaultSearchText]);
  useEffect(function () {
    setShowRecentSearchLabel(recentSearches.length > 0 && !searchText);
  }, [recentSearches.length, searchText]);
  useEffect(function () {
    var _options = (searchText && (options === null || options === void 0 ? void 0 : options.length) > 0 ? options : recentSearches).filter(function (option) {
      return option.toUpperCase().includes(searchText.toUpperCase());
    });
    if (showRecentSearchLabel) {
      _options.unshift(RECENT_SEARCH_LABEL);
    }
    setFilterOptions(_options);
  }, [searchText, showRecentSearchLabel, recentSearches.length, options.length]);
  useEffect(function () {
    clearFocusMenuInfo();
  }, [searchText]);
  useEffect(function () {
    var handleFocus = function handleFocus() {
      if (inputRef.current === document.activeElement) {
        setClickFocus(true);
      }
    };
    window.addEventListener('focus', handleFocus);
    return function () {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
  useEffect(function () {
    // clean up in case the user exits during timeout
    return function () {
      if (searchTimeoutHandle.current !== undefined) {
        clearTimeout(searchTimeoutHandle.current);
        searchTimeoutHandle.current = undefined;
      }
    };
  }, []);
  var closeOptionMenu = function closeOptionMenu() {
    setOptionsMenuOpen(false);
    clearFocusMenuInfo();
  };
  var autoFocusSearch = function autoFocusSearch() {
    inputRef.current.focus();
    if (clearBtnTriggerBehavior.current === 'click') {
      setClickFocus(true);
    } else {
      setOptionsMenuOpen(true);
      enterTarget.current = 'clearBtn';
    }
    clearBtnTriggerBehavior.current = '';
  };
  var handleClearText = function handleClearText() {
    setSearchText('');
    handleSearch();
    autoFocusSearch();
  };
  var clearFocusMenuInfo = function clearFocusMenuInfo() {
    setFocusOptionInfo({
      text: '',
      deleteFocus: false
    });
  };
  var handleSearch = function handleSearch() {
    var searchString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    searchString && setLocalRecentSearches(searchString, 'add');
    onSearch === null || onSearch === void 0 || onSearch(searchString === null || searchString === void 0 ? void 0 : searchString.trim());
  };
  var scrollItemIntoView = function scrollItemIntoView(index) {
    if (index < 0) return;
    if (showRecentSearchLabel && index === 1) {
      var _document$querySelect;
      // to keep the recent search label in view after scrolling from the last item to the first
      (_document$querySelect = document.querySelector("#EmbersSearchOptionItem-".concat(index))) === null || _document$querySelect === void 0 || _document$querySelect.scrollIntoView({
        block: 'end',
        behavior: 'smooth'
      });
    } else {
      scrollMenuItemIntoView("EmbersSearchOptionItem-".concat(index));
    }
  };
  var moveOption = function moveOption(direction) {
    if (!filterOptions.length) return;
    var hasLabelInOptions = filterOptions[0] === RECENT_SEARCH_LABEL;
    var firstIndex = hasLabelInOptions ? 1 : 0;
    var optionsLength = filterOptions.length;
    if (!focusOptionInfo.text) {
      if (direction === 'down') {
        // if nothing selected, set first option active
        setFocusOptionInfo(function (pre) {
          return _objectSpread(_objectSpread({}, pre), {}, {
            text: filterOptions[firstIndex]
          });
        });
      }
      return;
    }
    var nextIndex = firstIndex;
    if (direction === 'down') {
      nextIndex = filterOptions.indexOf(focusOptionInfo.text) + 1;
      if (nextIndex > optionsLength - 1) nextIndex = firstIndex;
    } else {
      nextIndex = filterOptions.indexOf(focusOptionInfo.text) - 1;
      if (nextIndex < firstIndex) nextIndex = optionsLength - 1;
    }
    setFocusOptionInfo(function () {
      return {
        text: filterOptions[nextIndex],
        deleteFocus: false
      };
    });
    scrollItemIntoView(nextIndex);
  };
  var updateDeleteBtnFocus = function updateDeleteBtnFocus(focus) {
    // the delete btn will display only when search text is null
    var optionDeletableIsFocused = !searchText && focusOptionInfo.text;
    optionDeletableIsFocused && setFocusOptionInfo(function (pre) {
      return _objectSpread(_objectSpread({}, pre), {}, {
        deleteFocus: focus
      });
    });
  };
  var handleKeyUp = function handleKeyUp(e) {
    var searchString;
    switch (e.key) {
      case 'ArrowDown':
        moveOption('down');
        break;
      case 'ArrowUp':
        moveOption('up');
        break;
      case 'ArrowLeft':
        updateDeleteBtnFocus(false);
        break;
      case 'ArrowRight':
        updateDeleteBtnFocus(true);
        break;
      case ' ':
      case 'Enter':
        // delete recent
        if (focusOptionInfo.text && focusOptionInfo.deleteFocus) {
          setLocalRecentSearches(focusOptionInfo.text, 'del');
          clearFocusMenuInfo();
          return;
        }
        if (e.key === 'Enter') {
          if (enterTarget.current === 'clearBtn') {
            enterTarget.current = '';
            return;
          }
          // search
          searchString = searchText;
          if (focusOptionInfo.text) {
            setSearchText(focusOptionInfo.text);
            searchString = focusOptionInfo.text;
          }
          var trimSearchString = searchString.trim();
          if (trimSearchString) {
            handleSearch(trimSearchString);
            closeOptionMenu();
          }
        }
        break;
    }
  };
  var setLocalRecentSearches = function setLocalRecentSearches(text, action) {
    var recentSearch = getLocalRecentSearches();
    var textExist = recentSearch.includes(text);
    if (action === 'add') {
      if (!textExist) {
        recentSearch.unshift(text);
        recentSearch.length > 10 && recentSearch.pop();
      } else {
        var index = recentSearch.indexOf(text);
        recentSearch = [text].concat(_toConsumableArray(recentSearch.slice(0, index)), _toConsumableArray(recentSearch.slice(index + 1, recentSearch.length)));
      }
    } else {
      textExist && recentSearch.splice(recentSearch.indexOf(text), 1);
    }
    setRecentSearches(recentSearch);
    localStorage.setItem(storageKey, recentSearch.join(','));
  };
  var renderOptionLabel = function renderOptionLabel(option) {
    // to bold the currently typed text
    var optionDisplay;
    var start = option.toUpperCase().indexOf(searchText.toUpperCase());
    if (searchText && start !== -1) {
      var end = start + searchText.length;
      optionDisplay = [/*#__PURE__*/React.createElement("span", {
        key: "k0"
      }, option.substring(0, start)), /*#__PURE__*/React.createElement("b", {
        key: "k1"
      }, option.substring(start, end)), /*#__PURE__*/React.createElement("span", {
        key: "k2"
      }, option.substring(end, option.length))];
    } else {
      optionDisplay = option;
    }
    return optionDisplay;
  };
  var renderSearchBar = function renderSearchBar() {
    var focusByKeyboard = focus && !clickFocus;
    var trimSearchText = searchText.trim();
    return /*#__PURE__*/React.createElement("div", {
      className: classnames('EmbersSearch-bar', {
        'EmbersSearch-bar-focus': focus,
        'EmbersSearch-bar-focus-by-click': clickFocus,
        'EmbersSearch-bar-focus-by-keyboard': focusByKeyboard,
        'EmbersSearch-bar-focus-by-keyboard-whole': focusByKeyboard && !focusOptionInfo.text && !trimSearchText,
        'EmbersSearch-bar-focus-by-keyboard-separate': focusByKeyboard && !focusOptionInfo.text && trimSearchText,
        'EmbersSearch-bar-focus-by-keyboard-focus-menu': focusByKeyboard && !!focusOptionInfo.text,
        'EmbersSearch-bar-rest': !clickFocus
      }),
      ref: optionsMenuRef
    }, /*#__PURE__*/React.createElement(TextField, {
      inputRef: inputRef,
      inputProps: {
        role: 'combobox',
        'aria-expanded': isExpand ? 'true' : 'false',
        'aria-controls': searchListboxId,
        'aria-autocomplete': 'list',
        'aria-activedescendant': focusOptionInfo.text ? "EmbersSearchOptionItem-".concat(filterOptions.findIndex(function (v) {
          return v === focusOptionInfo.text;
        })) : undefined
      },
      className: "EmbersSearch-input",
      placeholder: placeholder || intl.formatMessage({
        id: 'embers.search.placeholder',
        defaultMessage: 'Search'
      }),
      value: searchText,
      autoComplete: "off",
      onChange: function onChange(event) {
        if (event.target.value === ' ' && focusOptionInfo.deleteFocus) return; // space bar trigger delete recent history item so return here
        setSearchText(event.target.value);
        if (searchTimeoutHandle.current) {
          clearTimeout(searchTimeoutHandle.current);
        }
        // auto search when the value has been deleted
        if (event.target.value === '') {
          searchTimeoutHandle.current = setTimeout(function () {
            handleSearch();
          }, 300);
        }
        focus && setOptionsMenuOpen(true);
      },
      onMouseDown: function onMouseDown() {
        setClickFocus(true);
      },
      onFocus: function onFocus() {
        setFocus(true);
        setOptionsMenuOpen(true);
      },
      onBlur: function onBlur() {
        setClickFocus(false);
        setFocus(false);
      },
      onKeyUp: handleKeyUp
    }), trimSearchText && /*#__PURE__*/React.createElement(ActionButton, {
      className: 'EmbersSearch-clear-btn',
      onMouseDown: function onMouseDown() {
        // Distinguishing whether the button is triggered by clicking or enter
        clearBtnTriggerBehavior.current = 'click';
      },
      onClick: handleClearText,
      iconName: "icon-close",
      label: intl.formatMessage({
        id: 'embers.serachBar.iconLabel.clear',
        defaultMessage: 'Clear search'
      })
    }), /*#__PURE__*/React.createElement(ActionButton, {
      className: classnames('EmbersSearch-search-btn', {
        active: trimSearchText
      }),
      onClick: function onClick() {
        if (trimSearchText) {
          handleSearch(trimSearchText);
          closeOptionMenu();
        }
      },
      iconName: "icon-search",
      tabIndex: trimSearchText ? 0 : -1,
      label: intl.formatMessage({
        id: 'embers.searchBar.iconLabel.search',
        defaultMessage: 'Search'
      })
    }));
  };
  var renderMenuItems = function renderMenuItems() {
    return filterOptions.map(function (option, index) {
      if (option === RECENT_SEARCH_LABEL) {
        return /*#__PURE__*/React.createElement(EmbersMenuItem, {
          className: "EmbersSearch-label",
          key: option,
          value: option,
          role: "none",
          tabIndex: -1,
          clickable: false
        }, searchTitle || intl.formatMessage({
          id: 'embers.search.label',
          defaultMessage: 'Recent Searches'
        }));
      }
      var focusOnDeleteBtn = focusOptionInfo.text === option && focusOptionInfo.deleteFocus;
      return /*#__PURE__*/React.createElement(EmbersMenuItem, {
        "aria-posinset": index,
        "aria-setsize": optionCount,
        "aria-selected": undefined,
        isActiveDescendent: focusOptionInfo.text === option,
        deleteBtnHasFocus: focusOnDeleteBtn,
        id: "EmbersSearchOptionItem-".concat(index),
        key: option,
        value: option,
        prefixIcon: searchText ? /*#__PURE__*/React.createElement(React.Fragment, null) : /*#__PURE__*/React.createElement("i", {
          className: "icon-past"
        }),
        deletable: searchText === '',
        onDelete: function onDelete(value) {
          setLocalRecentSearches(value, 'del');
        },
        onClick: function onClick() {
          setSearchText(option);
          handleSearch(option);
          closeOptionMenu();
        }
      }, renderOptionLabel(option));
    });
  };
  return /*#__PURE__*/React.createElement(SearchContainer, {
    className: classnames('EmbersSearch-root', className)
  }, renderSearchBar(), /*#__PURE__*/React.createElement(StyledMenu, {
    id: searchListboxId,
    open: isExpand,
    menuScrollProps: {
      maxMenuNumberInView: showRecentSearchLabel ? 6 : 5
    },
    anchorEl: optionsMenuRef.current,
    fullWidth: true,
    onClose: closeOptionMenu
    // NOTE:  the `key` here is so that when menu length / maxMenuNumberInView changes it re-renders
    ,
    key: "menuKey_".concat(showRecentSearchLabel ? 'recent_options' : 'options', "_").concat(filterOptions.length)
  }, renderMenuItems()), /*#__PURE__*/React.createElement("span", {
    role: "status",
    "aria-live": "polite",
    style: visuallyHidden
  }, focusOptionInfo.deleteFocus ? "".concat(intl.formatMessage({
    id: 'embers.iconLabel.delete',
    defaultMessage: 'delete'
  }), " ").concat(intl.formatMessage({
    id: 'embers.autocomplete.assitive.role.button',
    defaultMessage: 'button'
  })) : ''));
};